import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogTitle,
} from "@ac-expo/ui-components";
import { resolveAuthModalAction } from "@ac-expo/utils";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import useGetParams from "@web-app/src/hooks/useGetParams";
import AuthStore from "@web-app/src/services/AuthStore";
import {
  AUTH_MODAL_POSSIBILITIES,
  AuthModalPossibilities,
} from "@web-app/src/types/authModalPossibilities";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { JSX, useCallback, useEffect, useRef, useState } from "react";
import { VisuallyHidden } from "react-aria";
import ForgotPasswordConfirmation from "../ForgotPasswordConfirmation";
import ForgotPasswordForm from "../ForgotPasswordForm";
import LoginForm from "../LoginForm";
import SignUpForm from "../SignUpForm";

const AuthModal = observer(() => {
  const { user, isLoading, isAuthenticated, disconnect } = useUserContext();
  const router = useRouter();

  const [step, setStep] = useState<AuthModalPossibilities>(
    AuthStore.getInitialStep()
  );
  const [isClosingManually, setIsClosingManually] = useState(false);
  const { email, goto, step: stepQuery, type } = useGetParams();
  const disconnectedForEmailRef = useRef<string | null>(null);

  const open = AuthStore.getModalIsVisible();
  // The URL email (invitations, guest links) wins over an email typed on the page.
  const baseEmail = email ?? AuthStore.getPrefilledEmail() ?? undefined;

  useEffect(() => {
    if (open) {
      setStep(AuthStore.getInitialStep());
    }
  }, [open]);

  useEffect(() => {
    if (email && !isLoading && !isClosingManually) {
      // A leftover guest-checkout cookie can yield a `user` while Better Auth has no
      // session: treat that as logged out, otherwise pushing `goto` bounces against
      // the page's own auth redirect forever.
      const action = resolveAuthModalAction({
        email,
        hasSession: isAuthenticated && !!user,
        userEmail: user?.email,
        goto,
        alreadyDisconnectedForEmail: disconnectedForEmailRef.current === email,
      });

      switch (action.type) {
        case "open-modal":
          AuthStore.setModalVisible(true);
          break;
        case "disconnect":
          disconnectedForEmailRef.current = email;
          disconnect();
          break;
        case "goto":
          router.push(action.path);
          break;
        case "none":
          break;
      }

      if (
        stepQuery &&
        stepQuery !== step &&
        AUTH_MODAL_POSSIBILITIES.includes(stepQuery as AuthModalPossibilities)
      ) {
        setStep(stepQuery as AuthModalPossibilities);
      }
    }
  }, [
    disconnect,
    email,
    goto,
    isAuthenticated,
    isLoading,
    router,
    user,
    step,
    stepQuery,
    isClosingManually,
  ]);

  useEffect(() => {
    if (!email) {
      setIsClosingManually(false);
    }
  }, [email]);

  function onSwitchSteps(step: AuthModalPossibilities): void {
    setStep(step);
  }

  const onClose = useCallback((): void => {
    setIsClosingManually(true);

    const { pathname, query } = router;
    const paramsToRemove = ["email", "goto", "step", "type", "invitationId"];
    const newQuery = Object.fromEntries(
      Object.entries(query).filter(([key]) => !paramsToRemove.includes(key))
    );

    router.replace(
      {
        pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true }
    );

    AuthStore.setPendingAction(null);
    AuthStore.setModalVisible(false);
    setStep("LOGIN");
  }, [router]);

  const onLoginSuccess = useCallback(() => {
    AuthStore.consumePendingAction()?.();
    onClose();
  }, [onClose]);

  type Step = {
    title: string;
    content: JSX.Element;
  };

  const steps: Record<AuthModalPossibilities, Step> = {
    LOGIN: {
      title: "Connexion",
      content: (
        <LoginForm
          onSwitchSteps={onSwitchSteps}
          onClose={onLoginSuccess}
          baseEmail={baseEmail}
          goto={goto}
        />
      ),
    },
    SIGNUP: {
      title: "Inscription",
      content: (
        <SignUpForm
          onSwitchSteps={onSwitchSteps}
          onClose={onLoginSuccess}
          baseEmail={email}
          goto={goto}
          isFromTickets={type === "validation"}
        />
      ),
    },
    RESETPASSWORD: {
      title: "Mot de passe oublié",
      content: (
        <ForgotPasswordForm onSwitchSteps={onSwitchSteps} onClose={onClose} />
      ),
    },
    RESETPASSWORDCONFIRMATION: {
      title: "Confirmation",
      content: <ForgotPasswordConfirmation />,
    },
  };

  if (!open) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <VisuallyHidden>
        <DialogTitle>Connexion</DialogTitle>
      </VisuallyHidden>
      <DialogOverlay />
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[calc(100vh-60px)] max-w-[calc(100vw-20px)] overflow-auto bg-white md:max-h-[calc(100vh-120px)] md:max-w-[760px]"
      >
        {steps[step].content}
      </DialogContent>
    </Dialog>
  );
});

export default AuthModal;
